import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";

export type SegmentedToggleOption<T extends string | number | boolean> = {
  readonly value: T;
  readonly label: string;
  readonly activeColor: "success" | "error" | "warning";
  readonly hint?: string;
};

type SegmentedToggleProps<T extends string | number | boolean> = {
  readonly options: ReadonlyArray<SegmentedToggleOption<T>>;
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly label?: string;
  readonly disabled?: boolean;
};

const activeColorTokens = {
  success: {
    bg: "#C8E6C9",
    color: "#1b5e20",
    border: "#a5d6a7",
  },
  error: {
    bg: "#FFCDD2",
    color: "#b71c1c",
    border: "#ef9a9a",
  },
  warning: {
    bg: "#FFE0B2",
    color: "#e65100",
    border: "#ffcc80",
  },
};

const selectedColorStyles = Object.fromEntries(
  Object.entries(activeColorTokens).map(([key, tokens]) => [
    key,
    {
      "&.Mui-selected": {
        backgroundColor: tokens.bg,
        color: tokens.color,
        borderColor: tokens.border,
        fontWeight: 500,
        "&:hover": {
          backgroundColor: tokens.bg,
        },
      },
    },
  ]),
) as Record<keyof typeof activeColorTokens, object>;

const useStyles = makeStyles({
  toggleButtonGroup: {
    borderRadius: 2,
  },
  toggleButton: {
    textTransform: "none",
    fontWeight: 400,
    "&.Mui-selected.Mui-disabled": {
      backgroundColor: "action.disabledBackground",
      color: "text.disabled",
      borderColor: "divider",
      fontWeight: 500,
    },
  },
});

function SegmentedToggle<T extends string | number | boolean>({
  options,
  value,
  onChange,
  label,
  disabled = false,
}: SegmentedToggleProps<T>) {
  const styles = useStyles(useTheme());
  const activeOption = options.find((o) => o.value === value);

  return (
    <Box>
      {label && (
        <Typography variant="body2" mb={1}>
          {label}
        </Typography>
      )}
      <ToggleButtonGroup
        exclusive
        value={value}
        onChange={(_, newValue) => {
          if (newValue !== null) {
            onChange(newValue);
          }
        }}
        disabled={disabled}
        fullWidth
        size="small"
        sx={styles.toggleButtonGroup}
      >
        {options.map((opt) => (
          <ToggleButton
            key={String(opt.value)}
            value={opt.value}
            sx={[styles.toggleButton, selectedColorStyles[opt.activeColor]]}
          >
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {activeOption?.hint && (
        <Typography
          variant="caption"
          color="text.secondary"
          mt={0.5}
          display="block"
        >
          {activeOption.hint}
        </Typography>
      )}
    </Box>
  );
}

export default SegmentedToggle;
